import { useState } from 'react'
import type { SyncRow, SyncRun, SyncScan, SyncSide } from '@fitting-room/contracts'
import { AlertTriangle, ChevronLeft, RefreshCw } from 'lucide-react'
import { Link } from 'react-router'
import { PageHeader } from '../../components/admin/PageHeader'
import { useApplyGownSync, useGownSyncRun, useScanGownSync } from '../../lib/adminQueries'
import { useStaffToken } from '../../lib/staffToken'
import { NewWordsPanel } from './words/NewWordsPanel'
import { CATALOG_LABEL } from './words/words'

/**
 * Sync gowns — the boutique's Airtable, read and then applied.
 *
 * Two steps, and the split is the whole screen. The push goes in as
 * `mode: 'full'`, which discontinues every style Airtable no longer lists, and
 * a truncated page produces exactly the same request as a season change. So
 * nothing is written until a stylist has looked at the rows that would go, one
 * by one, in red, at the top of the table where she cannot scroll past them.
 *
 * She is not asked to confirm them. She is shown them, and if she disagrees
 * with one she puts it back by hand on the catalog page afterwards — a
 * checkbox per row would turn a decision about her own shop into a form, and
 * a form is a thing people learn to tick through.
 */

/**
 * A sync that would not run, said to the person standing in front of it.
 *
 * The message itself is written for the reader — it names the missing setting, or the
 * count that looked wrong — but it used to be followed by the error's own code
 * in brackets. `(airtable_not_configured)` is a thing to grep the source for,
 * not a thing to read at work: it tells a stylist nothing she can act on and
 * makes a sentence she could have acted on look like a crash.
 *
 * What she can act on is knowing whose problem it is, so that is what the
 * second line says.
 */
function SyncError({ message }: { readonly message: string }) {
  return (
    <div className="st-alert mb-5">
      <p>{message}</p>
      <p className="mt-1 text-xs opacity-70">If this keeps happening, please contact your system developer.</p>
    </div>
  )
}

const CATALOGUE_COPY = {
  catalogue: {
    title: 'Made to order',
    unit: 'styles',
    retire: 'Would be discontinued',
    blurb: 'What the atelier can cut. Its own list, keyed on the style number.',
  },
  rail: {
    title: 'Off the rack',
    unit: 'pieces',
    retire: 'Would be withdrawn',
    blurb: 'The gowns physically hanging in the store. Its own list, keyed on the barcode — no style is shared with the one above.',
  },
} as const

/**
 * How many unremarkable rows a table prints before summarising the rest.
 *
 * A first sync is 396 new styles, and a page of 396 identical "new" rows is a
 * page nobody reads to the bottom of — including past the part that matters.
 * It applies to new and changed rows ONLY. The retirements are never truncated
 * at any length: they are the reason this screen exists, and "…and 180 more"
 * where a gown's name should be is precisely the silence it is here to break.
 */
const ROWS_SHOWN = 25

/** A full backfill is 3.35 GB and a nightly one is a handful of files. */
function size(bytes: number): string {
  return bytes >= 1024 ** 3 ? `${(bytes / 1024 ** 3).toFixed(2)} GB` : `${Math.round(bytes / 1024 ** 2)} MB`
}

function photoNote(row: SyncRow): string {
  const parts: string[] = []
  if (row.photosAdded > 0) parts.push(`+${row.photosAdded}`)
  if (row.photosRemoved > 0) parts.push(`−${row.photosRemoved}`)
  return parts.join(' ')
}

function ComparisonTable({ side }: { readonly side: SyncSide }) {
  const copy = CATALOGUE_COPY[side.key]
  const created = side.created.slice(0, ROWS_SHOWN)
  const updated = side.updated.slice(0, ROWS_SHOWN)

  if (side.absent.length === 0 && side.created.length === 0 && side.updated.length === 0) {
    return <p className="st-meta">Nothing would change.</p>
  }

  return (
    <div className="st-card overflow-x-auto">
      <table className="st-table">
        <thead>
          <tr>
            <th>{side.key === 'rail' ? 'Piece' : 'Style'}</th>
            <th>What would happen</th>
            <th>Fields</th>
            <th>Photos</th>
          </tr>
        </thead>
        <tbody>
          {/* First, and never truncated: the only rows on this screen that
              take something away. */}
          {side.absent.map((row) => (
            <tr key={`absent-${row.ref}`} data-tone="alert">
              <td className="font-medium">{row.label}</td>
              <td>
                <span className="st-badge st-badge--alert">{copy.retire}</span>
              </td>
              <td className="st-meta">no longer in Airtable</td>
              <td />
            </tr>
          ))}

          {created.map((row) => (
            <tr key={`new-${row.ref}`}>
              <td className="font-medium">{row.label}</td>
              <td>
                <span className="st-badge st-badge--submitted">New</span>
              </td>
              <td className="st-meta">not in the catalog yet</td>
              <td className="st-meta tabular-nums">{photoNote(row)}</td>
            </tr>
          ))}
          {side.created.length > created.length ? (
            <tr>
              <td className="st-meta" colSpan={4}>
                …and {side.created.length - created.length} more new {copy.unit}.
              </td>
            </tr>
          ) : null}

          {updated.map((row) => (
            <tr key={`changed-${row.ref}`}>
              <td className="font-medium">{row.label}</td>
              <td>
                <span className="st-badge st-badge--open">Changed</span>
              </td>
              <td>{row.fields.length > 0 ? row.fields.join(', ') : <span className="st-meta">photos only</span>}</td>
              <td className="st-meta tabular-nums">{photoNote(row)}</td>
            </tr>
          ))}
          {side.updated.length > updated.length ? (
            <tr>
              <td className="st-meta" colSpan={4}>
                …and {side.updated.length - updated.length} more changed {copy.unit}.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  )
}

function SideSection({ side }: { readonly side: SyncSide }) {
  const copy = CATALOGUE_COPY[side.key]
  return (
    <section className="mb-7">
      <h2 className="st-eyebrow mb-1">{copy.title}</h2>
      <p className="st-meta mb-2 max-w-2xl">{copy.blurb}</p>
      <p className="st-meta mb-2 tabular-nums">
        {/* `heldRows` counts what a sweep could touch, so already-retired rows
            are not in it — which is what makes the share below meaningful. */}
        {side.upstreamRows} in Airtable · {side.heldRows} live here · {side.created.length} new ·{' '}
        {side.updated.length} changed ·{' '}
        <span className={side.absent.length > 0 ? 'font-medium' : undefined}>
          {side.absent.length} would be retired
        </span>{' '}
        · photos +{side.photosNew} −{side.photosGone}
      </p>
      <ComparisonTable side={side} />
    </section>
  )
}

/**
 * The apply, while it is happening and after it has.
 *
 * Shown from the run record rather than from what the button returned, because
 * the run outlives the tab that started it: a stylist who locked her screen
 * during a five-minute backfill comes back to this panel, not to a spinner
 * that lost its place.
 */
function RunPanel({ run }: { readonly run: SyncRun }) {
  if (run.status === 'failed') {
    /*
     * What it says depends on how far it got, because "nothing was written" is
     * a claim and not a comfort. The read and both guards run before a
     * credential is minted, so a failure there really did write nothing; a
     * failure during the push did not, and telling her otherwise is how a
     * half-applied catalog goes unnoticed.
     */
    const wrote =
      run.phase === 'reading'
        ? 'Nothing was written.'
        : run.phase === 'landing'
          ? 'Some photos may have been stored. No gown changed.'
          : 'This stopped while pushing the catalog, so part of it may have gone in. Scan again to see where it got to.'
    return (
      <div className="st-alert mb-5">
        <p className="font-medium">{wrote}</p>
        <p className="mt-1">{run.error}</p>
      </div>
    )
  }

  if (run.status === 'running') {
    return (
      <div className="st-notice mb-5">
        <p className="font-medium">
          {run.phase === 'reading'
            ? 'Reading Airtable…'
            : run.phase === 'landing'
              ? 'Fetching photos…'
              : 'Pushing the catalog…'}
        </p>
        <p className="mt-1 tabular-nums">
          {run.photos.landed} of {run.photos.seen} photos stored
          {run.photos.skipped > 0 ? ` · ${run.photos.skipped} dropped` : ''} ·{' '}
          {size(run.photos.bytesUploaded)} uploaded
        </p>
        <p className="st-meta mt-1">
          This keeps running if you close the tab. Come back to this page to see where it got to.
        </p>
      </div>
    )
  }

  return (
    <div className="st-ok mb-5">
      <p className="font-medium">Synced.</p>
      <p className="mt-1 tabular-nums">
        Made to order: {run.catalogue?.created ?? 0} new, {run.catalogue?.updated ?? 0} updated,{' '}
        {run.catalogue?.discontinued ?? 0} discontinued. Off the rack: {run.rail?.created ?? 0} new,{' '}
        {run.rail?.updated ?? 0} updated, {run.rail?.withdrawn ?? 0} withdrawn.
      </p>
      <p className="mt-1 tabular-nums">
        {run.photos.landed} photos stored
        {run.photos.skipped > 0 ? `, ${run.photos.skipped} dropped` : ''}.
      </p>
      {/* Not a warning about the run: it worked. These gowns went in without
          one detail each, and the next scan is where they get it back. */}
      {run.unmappedWords.length > 0 ? (
        <details className="mt-2">
          <summary className="cursor-pointer">
            {run.unmappedWords.length} {run.unmappedWords.length === 1 ? 'word' : 'words'} from Airtable had no
            match, so the gowns using them synced without that detail. Scan again to match them.
          </summary>
          <ul className="mt-1 list-disc pl-5">
            {run.unmappedWords.map((word) => (
              <li key={`${word.catalog}-${word.attribute}-${word.wording}`}>
                {CATALOG_LABEL[word.catalog]}, {word.attribute}: &ldquo;{word.wording}&rdquo; ({word.styles.length})
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      {run.warnings.length > 0 ? (
        <details className="mt-2">
          <summary className="cursor-pointer">{run.warnings.length} warnings</summary>
          <ul className="mt-1 list-disc pl-5">
            {run.warnings.slice(0, 20).map((warning, index) => (
              <li key={`${index}-${warning}`}>{warning}</li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  )
}

export function SyncPage() {
  const { token, role } = useStaffToken()
  // Drawing only. The endpoint is the gate; see the note beside the button.
  const canApply = role === 'owner'
  const scan = useScanGownSync(token)
  const apply = useApplyGownSync(token)
  const runQuery = useGownSyncRun(token)
  const [result, setResult] = useState<SyncScan | null>(null)

  const run = runQuery.data?.run ?? null
  const busy = run?.status === 'running'
  // The table describes the catalog as it was when it was read. Once a run
  // has written to it, every count on screen is about a past state, so the
  // comparison is dropped rather than left sitting there looking current.
  const applied = apply.isSuccess && run?.status === 'succeeded'

  const rescan = () => {
    apply.reset()
    scan.mutate(undefined, { onSuccess: setResult })
  }

  return (
    <div>
      {/* Back to the catalog this sync writes into. Sync gowns has no entry of
          its own in the Catalog sub-nav — it is the catalog's action — so the
          way back is drawn here, the way an order points back to its list. */}
      <Link to="/admin/catalogue" className="st-btn-quiet -ml-2 mb-3 inline-flex">
        <ChevronLeft size={14} aria-hidden />
        Catalog
      </Link>
      <PageHeader eyebrow="From the boutique's Airtable" title="Sync gowns">
        <button
          type="button"
          className="st-btn-ghost"
          disabled={scan.isPending || busy}
          onClick={rescan}
        >
          <RefreshCw size={14} aria-hidden />
          {scan.isPending ? 'Reading Airtable…' : result ? 'Scan again' : 'Scan'}
        </button>
      </PageHeader>

      <p className="st-meta mb-5 max-w-2xl">
        Scanning reads Airtable and writes nothing. It shows what a sync would change, including every gown
        that would be retired because Airtable no longer lists it. Nothing moves until you apply it.
      </p>

      {scan.isError ? <SyncError message={scan.error.message} /> : null}
      {apply.isError ? <SyncError message={apply.error.message} /> : null}

      {run ? <RunPanel run={run} /> : null}

      {result && !applied ? (
        <>
          {/*
           * A refusal, not a warning with a way past it. Both of these mean the
           * read cannot be believed, and there is no version of "retire 200 of
           * your 396 styles" that should be one click away on a busy morning.
           */}
          {result.refusals.map((refusal) => (
            <div key={`${refusal.code}-${refusal.catalogue}`} className="st-alert mb-4">
              <p className="flex items-center gap-2 font-medium">
                <AlertTriangle size={15} aria-hidden />
                This scan cannot be applied
              </p>
              <p className="mt-1">{refusal.message}</p>
            </div>
          ))}

          {/*
           * Above the button on purpose: a word ruled on before applying goes
           * in with the gowns, and one left for later goes in without its
           * detail — both fine, but the first is the one worth a glance.
           */}
          {result.unmappedWords.length > 0 && canApply ? (
            <NewWordsPanel staffToken={token} words={result.unmappedWords} onRuled={rescan} />
          ) : null}

          <div className="mb-6 flex flex-wrap items-center gap-3">
            {/*
              A stylist can read this whole page and cannot press this button.
              That split is deliberate — see `OWNER_ONLY` in the contracts
              package for the rule — and she is TOLD so rather than shown a
              control that answers 403, because a button that refuses is
              indistinguishable from a button that is broken.

              The endpoint refuses it regardless of what is drawn here; this is
              a courtesy, not the permission. `console-identity.test.ts` is
              where the permission lives.
            */}
            {canApply ? (
              <button
                type="button"
                className="st-btn"
                disabled={!result.applicable || busy || apply.isPending}
                onClick={() => apply.mutate({ scanId: result.scanId })}
              >
                Apply this sync
              </button>
            ) : (
              <span className="st-meta">
                Applying a sync is the owner&apos;s to press. Send them this page, or ask them to sign in here.
              </span>
            )}
            {result.catalogue.absent.length + result.rail.absent.length > 0 ? (
              <span className="st-meta">
                {result.catalogue.absent.length + result.rail.absent.length} gowns in red would be retired. You can
                put any of them back afterwards on the Catalog page.
              </span>
            ) : (
              <span className="st-meta">Nothing would be retired.</span>
            )}
          </div>

          <SideSection side={result.catalogue} />
          <SideSection side={result.rail} />
        </>
      ) : null}

      {!result && !run ? (
        <div className="st-card px-4 py-10 text-center">
          <p className="st-meta">Scan to see what a sync would change.</p>
        </div>
      ) : null}
    </div>
  )
}
